<template>
  <div class="protected-image-wrapper" :class="containerClass">
    <canvas
      v-show="ready && !error"
      ref="canvasRef"
      v-protected-image
      :class="[imgClass, { 'protected-image-obscured': isObscured }]"
      role="img"
      aria-label="Evidência de biometria facial"
    />
    <div v-if="loading" class="protected-image-state" role="status" aria-live="polite">
      <span class="protected-image-spinner" aria-hidden="true" />
      <span class="text-sm text-[#52526f] mt-2">Carregando evidência...</span>
    </div>
    <div v-else-if="error" class="protected-image-state" role="alert">
      <span class="text-sm text-[#7f1d1d]">Não foi possível carregar a evidência</span>
    </div>
    <div v-if="isObscured && ready && !error" class="protected-image-obscured-overlay">
      <span class="text-sm text-[#52526f] font-medium">Conteúdo protegido</span>
    </div>
    <div
      class="protected-image-shield"
      @contextmenu.prevent
      @dragstart.prevent
      @click="emit('click')"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount, nextTick } from 'vue'
import Network from '@/Network'
import { useScreenCaptureGuard } from '@/hooks/useScreenCaptureGuard'

const props = withDefaults(
  defineProps<{
    biometryId?: number
    blobUrl?: string
    rotation?: number
    imgClass?: string
    containerClass?: string
  }>(),
  { rotation: 0 }
)

const emit = defineEmits<{
  click: []
}>()

const { isObscured, protectCanvas } = useScreenCaptureGuard()

const canvasRef = ref<HTMLCanvasElement | null>(null)
const ready = ref(false)
const loading = ref(false)
const error = ref(false)

let bitmap: ImageBitmap | null = null

function drawBitmap() {
  const canvas = canvasRef.value
  if (!bitmap || !canvas) return
  const swap = props.rotation % 180 !== 0
  canvas.width = swap ? bitmap.height : bitmap.width
  canvas.height = swap ? bitmap.width : bitmap.height
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.save()
  ctx.translate(canvas.width / 2, canvas.height / 2)
  ctx.rotate((props.rotation * Math.PI) / 180)
  ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2)
  ctx.restore()
  protectCanvas(canvas)
}

function disposeBitmap() {
  if (bitmap) {
    bitmap.close()
    bitmap = null
  }
  ready.value = false
}

async function load(biometryId?: number, blobUrl?: string) {
  disposeBitmap()
  loading.value = true
  error.value = false
  try {
    let blob: Blob
    if (blobUrl) {
      const response = await fetch(blobUrl)
      if (!response.ok) throw new Error(`blob fetch failed: ${response.status}`)
      blob = await response.blob()
    } else if (biometryId) {
      blob = await Network.facialBiometry.getPhotoBlob(biometryId)
    } else {
      loading.value = false
      return
    }
    bitmap = await createImageBitmap(blob)
    ready.value = true
    await nextTick()
    drawBitmap()
  } catch {
    error.value = true
  } finally {
    loading.value = false
  }
}

watch(
  [() => props.biometryId, () => props.blobUrl],
  ([id, url]) => {
    if (url) load(undefined, url)
    else if (id) load(id)
    else disposeBitmap()
  },
  { immediate: true }
)

watch(
  () => props.rotation,
  () => {
    if (ready.value) drawBitmap()
  }
)

onBeforeUnmount(disposeBitmap)
</script>

<style>
.protected-image-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.protected-image-wrapper canvas {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.protected-image-shield {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: transparent;
  cursor: pointer;
}

.protected-image-state {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 0;
}

.protected-image-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #cfcfdd;
  border-top-color: #52526f;
  border-radius: 50%;
  animation: protected-image-spin 0.9s linear infinite;
}

@keyframes protected-image-spin {
  to {
    transform: rotate(360deg);
  }
}

.protected-image-obscured {
  filter: blur(30px);
  transition: filter 0.15s ease;
}

.protected-image-obscured-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(240, 240, 244, 0.7);
}

@media print {
  .protected-image-wrapper canvas {
    visibility: hidden !important;
  }

  .protected-image-wrapper::after {
    content: 'Imagem protegida - nao disponivel para impressao';
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: #7f7f8e;
    font-style: italic;
  }
}
</style>
